import React, { useEffect, useRef, useState } from 'react';
import {
    ActivityIndicator,
    Linking,
    Platform,
    ScrollView,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import colors from '@/constants/Colors';
import { useSupabase } from '@/hooks/useSupabase';
import { isPushAccountLoginPending, isPushLinkedToAccount } from '@/lib/onesignal-push';
import {
    serverStatusLabel,
    usePushRegistration,
} from '@/context/PushRegistrationProvider';

function Step({ number, title, description }: { number: number; title: string; description: string }) {
    return (
        <View style={styles.step}>
            <View style={styles.stepNumber}>
                <Text style={styles.stepNumberText}>{number}</Text>
            </View>
            <View style={styles.stepBody}>
                <Text style={styles.stepTitle}>{title}</Text>
                <Text style={styles.stepDescription}>{description}</Text>
            </View>
        </View>
    );
}

export default function EnableNotificationsScreen() {
    const router = useRouter();
    const insets = useSafeAreaInsets();
    const { user } = useSupabase();
    const {
        localState: state,
        serverStatus,
        locallyHealthy,
        isFullyHealthy,
        isHealing,
        heal,
    } = usePushRegistration();
    const [justSucceeded, setJustSucceeded] = useState(false);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (isFullyHealthy && !justSucceeded) {
            setJustSucceeded(true)
            closeTimerRef.current = setTimeout(() => {
                if (router.canGoBack()) {
                    router.back()
                } else {
                    router.replace('/(app)')
                }
            }, 2200)
        }
        return () => {
            if (closeTimerRef.current) {
                clearTimeout(closeTimerRef.current)
                closeTimerRef.current = null
            }
        }
    }, [isFullyHealthy, justSucceeded, router])

    useEffect(() => {
        void heal({ requestPermission: true })
    }, [heal])

    const handleEnable = async () => {
        const next = await heal({ requestPermission: true });

        if (!next?.permission) {
            try { await Linking.openSettings(); } catch { }
        }
    };

    const handleCheckAgain = async () => {
        await heal();
    };

    const handleClose = () => {
        if (router.canGoBack()) {
            router.back();
        } else {
            router.replace('/(app)');
        }
    };

    const { permission, optedIn, subscriptionId, pushToken, externalId } = state
    const isLinked = user?.id ? isPushLinkedToAccount(state, user.id) : Boolean(externalId)
    const loginPending = user?.id ? isPushAccountLoginPending(state, user.id) : false
    const deviceRegisteredOnServer = serverStatus === 'registered' && Boolean(subscriptionId && pushToken)
    const looksEnabledButUnregistered = permission && optedIn && !subscriptionId
    const looksRegisteredButUnlinked = Boolean(subscriptionId) && !isLinked

    if (justSucceeded) {
        return (
            <View style={[styles.successContainer, { paddingTop: insets.top + 24 }]}>
                <View style={styles.successCircle}>
                    <Text style={styles.successCheck}>✓</Text>
                </View>
                <Text style={styles.successTitle}>You're all set!</Text>
                <Text style={styles.successSubtitle}>
                    Push notifications are enabled. You'll now receive job alerts, messages and updates.
                </Text>
                <TouchableOpacity onPress={handleClose} style={styles.successButton}>
                    <Text style={styles.successButtonText}>Done</Text>
                </TouchableOpacity>
            </View>
        );
    }

    const isAndroid = Platform.OS === 'android';
    const isIos = Platform.OS === 'ios';

    return (
        <View style={[styles.container, { paddingTop: insets.top }]}>
            <View style={styles.header}>
                <TouchableOpacity onPress={handleClose} style={styles.headerClose}>
                    <Text style={styles.headerCloseText}>Close</Text>
                </TouchableOpacity>
            </View>

            <ScrollView
                contentContainerStyle={[
                    styles.scroll,
                    { paddingBottom: 24 + insets.bottom },
                ]}
                showsVerticalScrollIndicator={false}
            >
                <View style={styles.heroIcon}>
                    <Text style={styles.heroIconEmoji}>🔔</Text>
                </View>
                <Text style={styles.title}>Enable push notifications</Text>
                <Text style={styles.subtitle}>
                    Without notifications you'll miss new job offers, messages from the office, and
                    timesheet updates.
                </Text>

                <View style={styles.statusCard}>
                    <View style={styles.statusRow}>
                        <Text style={styles.statusLabel}>System notification permission</Text>
                        <Text style={[styles.statusValue, permission ? styles.statusOk : styles.statusBad]}>
                            {permission ? 'Allowed' : 'Blocked'}
                        </Text>
                    </View>
                    <View style={styles.statusRow}>
                        <Text style={styles.statusLabel}>Crewsaders push subscription</Text>
                        <Text style={[styles.statusValue, locallyHealthy ? styles.statusOk : styles.statusBad]}>
                            {locallyHealthy
                                ? 'Active'
                                : optedIn && subscriptionId
                                    ? 'Not linked'
                                    : optedIn
                                        ? 'Registering…'
                                        : 'Not active'}
                        </Text>
                    </View>
                    <View style={styles.statusRow}>
                        <Text style={styles.statusLabel}>Linked account</Text>
                        <Text style={[styles.statusValue, isLinked ? styles.statusOk : loginPending ? styles.statusPending : styles.statusBad]}>
                            {isLinked ? 'Yes' : loginPending ? 'Pending…' : externalId ? 'Wrong account' : 'No'}
                        </Text>
                    </View>
                    <View style={styles.statusRow}>
                        <Text style={styles.statusLabel}>Registered on server</Text>
                        <Text style={[styles.statusValue, deviceRegisteredOnServer ? styles.statusOk : serverStatus === 'idle' ? styles.statusPending : styles.statusBad]}>
                            {deviceRegisteredOnServer
                                ? 'Yes'
                                : serverStatus === 'idle'
                                    ? 'Checking…'
                                    : serverStatusLabel(serverStatus)}
                        </Text>
                    </View>
                    <View style={styles.statusRow}>
                        <Text style={styles.statusLabel}>Subscription ID</Text>
                        <Text style={[styles.statusValue, subscriptionId ? styles.statusOk : styles.statusBad]}>
                            {subscriptionId ? `${subscriptionId.substring(0, 8)}…` : 'None'}
                        </Text>
                    </View>
                </View>

                {loginPending && (
                    <Text style={styles.registrationHint}>
                        Your account is signed in but this device has not finished creating a push subscription yet.
                        Tap Enable below to register it.
                    </Text>
                )}

                {looksRegisteredButUnlinked && (
                    <Text style={styles.registrationHint}>
                        This device has a subscription but it is not linked to your Crewsaders account yet.
                        Tap Enable below to link it.
                    </Text>
                )}

                {looksEnabledButUnregistered && (
                    <Text style={styles.registrationHint}>
                        Permission is granted but this device has not finished registering with OneSignal yet.
                        Tap Enable below to retry.
                    </Text>
                )}

                <Text style={styles.sectionHeading}>How to enable</Text>

                {isAndroid && (
                    <View style={styles.steps}>
                        <Step
                            number={1}
                            title="Tap “Enable notifications” below"
                            description="This registers your device with Crewsaders and requests permission if needed."
                        />
                        <Step
                            number={2}
                            title="If blocked, open Settings"
                            description="Turn on Allow notifications for Crewsaders, then return here."
                        />
                        <Step
                            number={3}
                            title="(Samsung / OnePlus / Xiaomi only) Battery → Unrestricted"
                            description="Some phones aggressively kill apps in the background and block notifications. Set Crewsaders to Unrestricted in the Battery section."
                        />
                        <Step
                            number={4}
                            title="Come back to this screen"
                            description="It will detect the change automatically and confirm it's working."
                        />
                    </View>
                )}

                {isIos && (
                    <View style={styles.steps}>
                        <Step
                            number={1}
                            title="Tap “Enable notifications” below"
                            description="This registers your device with Crewsaders and requests permission if needed."
                        />
                        <Step
                            number={2}
                            title="If blocked, open Settings"
                            description="Turn on Allow Notifications for Crewsaders, then return here."
                        />
                        <Step
                            number={3}
                            title="Check Focus / Do Not Disturb"
                            description="If you have a Focus mode on, allow Crewsaders through it."
                        />
                        <Step
                            number={4}
                            title="Come back to this screen"
                            description="It will detect the change automatically and confirm it's working."
                        />
                    </View>
                )}

                <TouchableOpacity
                    onPress={handleEnable}
                    style={[styles.primaryButton, isHealing && styles.primaryButtonDisabled]}
                    disabled={isHealing}
                >
                    {isHealing ? (
                        <View style={styles.buttonRow}>
                            <ActivityIndicator color={colors.accent} size="small" />
                            <Text style={styles.primaryButtonText}>Registering device…</Text>
                        </View>
                    ) : (
                        <Text style={styles.primaryButtonText}>Enable notifications</Text>
                    )}
                </TouchableOpacity>

                <TouchableOpacity onPress={handleCheckAgain} style={styles.secondaryButton} disabled={isHealing}>
                    <Text style={styles.secondaryButtonText}>I've enabled it — check again</Text>
                </TouchableOpacity>

                <TouchableOpacity onPress={handleClose} style={styles.tertiaryButton}>
                    <Text style={styles.tertiaryButtonText}>Maybe later</Text>
                </TouchableOpacity>

                <Text style={styles.helpFooter}>
                    Still not working? Message the office team — they can run a delivery check on your
                    account.
                </Text>
            </ScrollView>
        </View>
    );
}

const styles = {
    container: {
        flex: 1,
        backgroundColor: colors.background,
    },
    header: {
        flexDirection: 'row' as const,
        justifyContent: 'flex-end' as const,
        paddingHorizontal: 16,
        paddingVertical: 12,
    },
    headerClose: {
        paddingHorizontal: 12,
        paddingVertical: 6,
    },
    headerCloseText: {
        color: '#666',
        fontSize: 16,
        fontWeight: '500' as const,
    },
    scroll: {
        paddingHorizontal: 24,
    },
    heroIcon: {
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: colors.accent,
        alignSelf: 'center' as const,
        alignItems: 'center' as const,
        justifyContent: 'center' as const,
        marginBottom: 16,
    },
    heroIconEmoji: {
        fontSize: 36,
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold' as const,
        textAlign: 'center' as const,
        color: '#000',
        marginBottom: 8,
    },
    subtitle: {
        fontSize: 15,
        textAlign: 'center' as const,
        color: '#444',
        lineHeight: 22,
        marginBottom: 24,
    },
    statusCard: {
        backgroundColor: '#f5f5f7',
        borderRadius: 12,
        padding: 16,
        marginBottom: 24,
        gap: 8,
    },
    statusRow: {
        flexDirection: 'row' as const,
        justifyContent: 'space-between' as const,
        alignItems: 'center' as const,
    },
    statusLabel: {
        fontSize: 13,
        color: '#666',
        flex: 1,
        paddingRight: 12,
    },
    statusValue: {
        fontSize: 13,
        fontWeight: '600' as const,
    },
    statusOk: {
        color: colors.success,
    },
    statusBad: {
        color: colors.error,
    },
    statusPending: {
        color: '#888',
    },
    registrationHint: {
        fontSize: 13,
        color: colors.error,
        lineHeight: 18,
        marginBottom: 16,
        textAlign: 'center' as const,
    },
    sectionHeading: {
        fontSize: 13,
        fontWeight: '600' as const,
        color: '#666',
        textTransform: 'uppercase' as const,
        letterSpacing: 0.5,
        marginBottom: 12,
    },
    steps: {
        gap: 16,
        marginBottom: 24,
    },
    step: {
        flexDirection: 'row' as const,
        alignItems: 'flex-start' as const,
        gap: 12,
    },
    stepNumber: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: colors.primary,
        alignItems: 'center' as const,
        justifyContent: 'center' as const,
    },
    stepNumberText: {
        color: colors.accent,
        fontWeight: 'bold' as const,
        fontSize: 14,
    },
    stepBody: {
        flex: 1,
    },
    stepTitle: {
        fontSize: 15,
        fontWeight: '600' as const,
        color: '#000',
        marginBottom: 2,
    },
    stepDescription: {
        fontSize: 13,
        color: '#555',
        lineHeight: 18,
    },
    primaryButton: {
        backgroundColor: colors.primary,
        paddingVertical: 16,
        borderRadius: 12,
        alignItems: 'center' as const,
        marginTop: 8,
    },
    primaryButtonDisabled: {
        opacity: 0.7,
    },
    primaryButtonText: {
        color: colors.accent,
        fontSize: 16,
        fontWeight: '600' as const,
    },
    buttonRow: {
        flexDirection: 'row' as const,
        alignItems: 'center' as const,
        gap: 8,
    },
    secondaryButton: {
        paddingVertical: 14,
        borderRadius: 12,
        alignItems: 'center' as const,
        marginTop: 8,
        borderWidth: 1,
        borderColor: '#ddd',
    },
    secondaryButtonText: {
        color: colors.primary,
        fontSize: 15,
        fontWeight: '500' as const,
    },
    tertiaryButton: {
        paddingVertical: 12,
        alignItems: 'center' as const,
        marginTop: 4,
    },
    tertiaryButtonText: {
        color: '#888',
        fontSize: 14,
    },
    helpFooter: {
        marginTop: 24,
        textAlign: 'center' as const,
        fontSize: 12,
        color: '#888',
        lineHeight: 18,
    },
    successContainer: {
        flex: 1,
        backgroundColor: colors.background,
        alignItems: 'center' as const,
        justifyContent: 'center' as const,
        paddingHorizontal: 24,
    },
    successCircle: {
        width: 96,
        height: 96,
        borderRadius: 48,
        backgroundColor: colors.success,
        alignItems: 'center' as const,
        justifyContent: 'center' as const,
        marginBottom: 24,
    },
    successCheck: {
        color: '#fff',
        fontSize: 56,
        fontWeight: 'bold' as const,
        lineHeight: 60,
    },
    successTitle: {
        fontSize: 22,
        fontWeight: 'bold' as const,
        color: '#000',
        marginBottom: 8,
    },
    successSubtitle: {
        fontSize: 14,
        color: '#444',
        textAlign: 'center' as const,
        lineHeight: 20,
    },
    successButton: {
        marginTop: 32,
        backgroundColor: colors.primary,
        paddingVertical: 14,
        paddingHorizontal: 48,
        borderRadius: 12,
    },
    successButtonText: {
        color: colors.accent,
        fontSize: 16,
        fontWeight: '600' as const,
    },
};
